import { formatDuration, formatDate } from './formatting.js';

/**
 * Turn a distance + speed into a duration/arrival estimate. Ship speed
 * (knots = nm/hr) already operates in game-world units, so `distanceNm /
 * speedKnots` is directly game-hours — no real-time/timeScale conversion
 * needed (see src/systems/NavigationSystem.js's _updateShipPosition for
 * the same relationship used server-side, and main.js's
 * _tickShipInterpolation for the client-side mirror of it).
 *
 * @param {number} distanceNm
 * @param {number} speedKnots
 * @param {Date} currentDate  GameState.currentDate — the in-game clock the ETA is relative to
 * @returns {{ hours:number, days:number, etaDate:Date }|null} null if either input is missing/non-positive
 */
export function estimateEta(distanceNm, speedKnots, currentDate) {
  if (!distanceNm || distanceNm <= 0 || !speedKnots || speedKnots <= 0) return null;
  const hours = distanceNm / speedKnots;
  const days = hours / 24;
  const etaDate = new Date(currentDate.getTime() + hours * 3600 * 1000);
  return { hours, days, etaDate };
}

/**
 * "2 days 6 hrs (Sep 04, 14:20)" — no "ETA:" label, since callers already
 * have their own label context (a table row's own <td>, a tooltip line
 * that adds its own prefix, etc).
 */
export function formatEta(distanceNm, speedKnots, currentDate) {
  const eta = estimateEta(distanceNm, speedKnots, currentDate);
  if (!eta) return null;
  return `${formatDuration(eta.days)} (${formatDate(eta.etaDate)})`;
}

// ---------- ship ETAs (Feedback #53) ----------
// Pure and EventBus-free so the server can import them too. All times are
// absolute timestamps against the in-game clock (`opts.now`, a Date).

const HOUR_MS = 3600000;

/**
 * Generic wording for every blocked ETA — players only need to know orders
 * are missing; the specific reason stays on `blockedReason` for tooltips.
 */
export const NO_ETA_TEXT = 'No ETA - awaiting orders';

const BLOCKED_DETAIL = {
  'awaiting-orders': 'Held in port with no work ordered to release the ship',
  'route-paused':    'Automated route is paused',
  'no-destination':  'No destination set',
  'no-route-data':   'Route distance unavailable',
  'no-speed':        'Ship cannot make way'
};

/** Specific, human-readable explanation of a `blockedReason` (or '' for none). */
export function describeEtaBlocked(reason) {
  return BLOCKED_DETAIL[reason] || '';
}

function blocked(blockedReason) {
  return { departAt: null, etaDate: null, holdHours: null, travelHours: null, blockedReason };
}

/** Speed for the leg: a ship leaving dry dock sails at condition 100. */
function _legSpeed(ship, departAt) {
  const dockEnd = ship.status === 'drydock' && ship.maintenance?.drydockUntil;
  return dockEnd ? ship.getCurrentSpeed(100) : ship.getCurrentSpeed();
}

function _build(ship, distanceNm, now, departAt) {
  const speed = _legSpeed(ship, departAt);
  if (!(distanceNm > 0)) return blocked('no-route-data');
  if (!(speed > 0)) return blocked('no-speed');
  const travelHours = distanceNm / speed;
  return {
    departAt,
    etaDate: new Date(departAt.getTime() + travelHours * HOUR_MS),
    holdHours: Math.max(0, (departAt.getTime() - now.getTime()) / HOUR_MS),
    travelHours,
    blockedReason: null
  };
}

/**
 * ETA for a ship's current plans: remaining leg if sailing, or hold time
 * plus the queued voyage if a departure is waiting in port.
 *
 * @param {Ship} ship
 * @param {object} opts
 * @param {Date}   opts.now          in-game clock (required)
 * @param {Map}    [opts.tradeRoutes] id -> TradeRoute; when given, a queued
 *   automated departure whose route is missing/disabled yields no ETA
 *   (mirrors NavigationSystem._updatePortHold, which drops it)
 * @returns {{ departAt:Date|null, etaDate:Date|null, holdHours:number|null,
 *             travelHours:number|null, blockedReason:string|null }}
 */
export function estimateShipEta(ship, opts = {}) {
  const now = opts.now;
  if (!ship || !(now instanceof Date)) return blocked('no-destination');

  if (ship.status === 'sailing' && ship.currentRoute) {
    const remaining = ship.currentRoute.distance * (1 - (ship.voyageProgress || 0));
    return _build(ship, remaining, now, now);
  }

  const pending = ship.pendingDeparture;
  if (!pending) return blocked('no-destination');

  if (pending.routeId && opts.tradeRoutes) {
    const route = opts.tradeRoutes.get(pending.routeId);
    if (!route || !route.enabled || route.shipId !== ship.id) return blocked('route-paused');
  }
  const leg = ship.getPendingRoute();
  if (!leg) return blocked('no-route-data');

  const departAt = ship.getPortReleaseDate(now);
  if (!departAt) return blocked('awaiting-orders');
  return _build(ship, leg.distanceNm, now, departAt);
}

/**
 * ETA for a voyage that has not been ordered yet (route-selection preview):
 * leaves when the ship is released from port, then sails `distanceNm`.
 * Same result shape as estimateShipEta.
 */
export function estimateDepartureEta(ship, distanceNm, opts = {}) {
  const now = opts.now;
  if (!ship || !(now instanceof Date)) return blocked('no-destination');
  const departAt = ship.status === 'sailing' ? now : ship.getPortReleaseDate(now);
  if (!departAt) return blocked('awaiting-orders');
  return _build(ship, distanceNm, now, departAt);
}

/**
 * Display text for an ETA result: "2 days 6 hrs (Sep 04, 2026 14:20)", with
 * ", incl. 3 hrs in port" when the ship is held first. Blocked results show
 * the generic NO_ETA_TEXT.
 */
export function formatShipEta(result) {
  if (!result || result.blockedReason || !result.etaDate) return NO_ETA_TEXT;
  const totalDays = (result.holdHours + result.travelHours) / 24;
  let text = `${formatDuration(totalDays)} (${formatDate(result.etaDate)})`;
  if (result.holdHours >= 0.5) text += `, incl. ${formatDuration(result.holdHours / 24)} in port`;
  return text;
}
